import { useEffect, useState } from 'react';
import cronstrue from 'cronstrue';

/**
 * Cron pickers are notoriously user-hostile. Rather than ship a single
 * `0 9 * * 1` field with no explanation, this widget exposes:
 *   - Five preset frequencies (manual / hourly / daily / weekly / monthly)
 *   - Time + day pickers that only render when relevant
 *   - An Advanced toggle for power users who want raw cron
 *   - A live "Runs every Monday at 09:00" description under the field
 *
 * The output is always a five-field POSIX cron string (or empty when
 * the workflow should run on-demand only). Parent owns the value; this
 * component is fully controlled.
 */

type Mode = 'manual' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'advanced';

const DOW = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

interface Props {
  value: string;
  onChange: (next: string) => void;
}

export function CronPicker({ value, onChange }: Props) {
  const parsed = parse(value);

  const [mode, setMode] = useState<Mode>(parsed.mode);
  const [hour, setHour] = useState<number>(parsed.hour);
  const [minute, setMinute] = useState<number>(parsed.minute);
  const [dow, setDow] = useState<number[]>(parsed.dow);
  const [dom, setDom] = useState<number>(parsed.dom);
  const [advanced, setAdvanced] = useState<string>(value || '0 9 * * 1');

  // Re-emit a cron string whenever the user changes anything.
  useEffect(() => {
    const cron = build(mode, { hour, minute, dow, dom, advanced });
    if (cron !== value) onChange(cron);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, hour, minute, dow, dom, advanced]);

  const human = describe(value);

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="flex flex-wrap gap-1.5">
        {(['manual', 'hourly', 'daily', 'weekly', 'monthly', 'advanced'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={[
              'px-2.5 py-1.5 rounded-md text-[12px] font-medium transition border',
              mode === m
                ? 'bg-accent-500 text-white border-accent-500 shadow-press'
                : 'bg-white text-ink-700 border-ink-200 hover:border-ink-300 dark:bg-ink-800 dark:text-ink-200 dark:border-ink-700',
            ].join(' ')}
          >
            {labelFor(m)}
          </button>
        ))}
      </div>

      {/* Mode-specific inputs */}
      {mode === 'manual' && (
        <p className="text-xs text-ink-500 dark:text-ink-400">
          Workflow will run only when triggered manually via the “Run now” button.
        </p>
      )}

      {mode === 'hourly' && (
        <Field label="Minute past the hour">
          <NumberInput value={minute} min={0} max={59} onChange={setMinute} />
        </Field>
      )}

      {mode === 'daily' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hour (24h)">
            <NumberInput value={hour} min={0} max={23} onChange={setHour} />
          </Field>
          <Field label="Minute">
            <NumberInput value={minute} min={0} max={59} onChange={setMinute} />
          </Field>
        </div>
      )}

      {mode === 'weekly' && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hour (24h)">
              <NumberInput value={hour} min={0} max={23} onChange={setHour} />
            </Field>
            <Field label="Minute">
              <NumberInput value={minute} min={0} max={59} onChange={setMinute} />
            </Field>
          </div>
          <Field label="Days of week">
            <div className="flex gap-1.5 flex-wrap">
              {DOW.map((d) => {
                const active = dow.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setDow(active ? dow.filter((x) => x !== d.value) : [...dow, d.value])}
                    className={[
                      'px-2.5 py-1.5 rounded-md text-[11.5px] font-semibold transition border',
                      active
                        ? 'bg-accent-500 text-white border-accent-500'
                        : 'bg-white text-ink-700 border-ink-200 hover:border-ink-300 dark:bg-ink-800 dark:text-ink-200 dark:border-ink-700',
                    ].join(' ')}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      )}

      {mode === 'monthly' && (
        <div className="grid grid-cols-3 gap-3">
          <Field label="Day of month">
            <NumberInput value={dom} min={1} max={28} onChange={setDom} />
          </Field>
          <Field label="Hour (24h)">
            <NumberInput value={hour} min={0} max={23} onChange={setHour} />
          </Field>
          <Field label="Minute">
            <NumberInput value={minute} min={0} max={59} onChange={setMinute} />
          </Field>
        </div>
      )}

      {mode === 'advanced' && (
        <Field label="Raw cron expression">
          <input
            value={advanced}
            onChange={(e) => setAdvanced(e.target.value)}
            placeholder="0 9 * * 1"
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent-500/30 dark:bg-ink-800 dark:border-ink-700 dark:text-ink-100"
          />
          <p className="mt-1 text-[11px] text-ink-500 dark:text-ink-400 font-mono">
            min · hr · day-of-month · month · day-of-week  (0–6, 0=Sun)
          </p>
        </Field>
      )}

      {/* Live preview */}
      <div className="rounded-md border border-ink-100 bg-paper-50 dark:bg-ink-800/60 dark:border-ink-700 px-3 py-2 text-[12.5px] text-ink-700 dark:text-ink-200 flex items-start gap-2">
        <span className="text-[10px] uppercase tracking-[0.14em] text-ink-500 dark:text-ink-400 font-semibold pt-0.5">Runs</span>
        <span className="flex-1">{human}</span>
        {value && (
          <code className="text-[11px] font-mono text-ink-500 dark:text-ink-400 shrink-0">{value}</code>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink-700 dark:text-ink-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

function NumberInput({
  value, min, max, onChange,
}: { value: number; min: number; max: number; onChange: (n: number) => void }) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => {
        const n = parseInt(e.target.value, 10);
        if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)));
      }}
      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-accent-500/30 dark:bg-ink-800 dark:border-ink-700 dark:text-ink-100"
    />
  );
}

function labelFor(m: Mode): string {
  switch (m) {
    case 'manual': return 'Manual';
    case 'hourly': return 'Hourly';
    case 'daily': return 'Daily';
    case 'weekly': return 'Weekly';
    case 'monthly': return 'Monthly';
    case 'advanced': return 'Advanced';
  }
}

function build(
  mode: Mode,
  o: { hour: number; minute: number; dow: number[]; dom: number; advanced: string },
): string {
  switch (mode) {
    case 'manual':
      return '';
    case 'hourly':
      return `${o.minute} * * * *`;
    case 'daily':
      return `${o.minute} ${o.hour} * * *`;
    case 'weekly': {
      const dow = (o.dow.length > 0 ? o.dow : [1]).slice().sort((a, b) => a - b).join(',');
      return `${o.minute} ${o.hour} * * ${dow}`;
    }
    case 'monthly':
      return `${o.minute} ${o.hour} ${o.dom} * *`;
    case 'advanced':
      return o.advanced.trim();
  }
}

function parse(cron: string): {
  mode: Mode; hour: number; minute: number; dow: number[]; dom: number;
} {
  const empty = { hour: 9, minute: 0, dow: [1], dom: 1 };
  if (!cron || !cron.trim()) return { mode: 'manual', ...empty };

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { mode: 'advanced', ...empty };

  const [min, hr, dom, mon, dow] = parts;
  const minNum = parseInt(min, 10);
  const hrNum = parseInt(hr, 10);
  const domNum = parseInt(dom, 10);

  // hourly: ` M * * * *`
  if (mon === '*' && dom === '*' && dow === '*' && hr === '*' && /^\d+$/.test(min)) {
    return { mode: 'hourly', hour: 9, minute: minNum, dow: [1], dom: 1 };
  }
  // daily: `M H * * *`
  if (mon === '*' && dom === '*' && dow === '*' && /^\d+$/.test(min) && /^\d+$/.test(hr)) {
    return { mode: 'daily', hour: hrNum, minute: minNum, dow: [1], dom: 1 };
  }
  // weekly: `M H * * d[,d]`
  if (mon === '*' && dom === '*' && /^\d+(,\d+)*$/.test(dow) && /^\d+$/.test(min) && /^\d+$/.test(hr)) {
    return {
      mode: 'weekly',
      hour: hrNum,
      minute: minNum,
      dow: dow.split(',').map((s) => parseInt(s, 10)),
      dom: 1,
    };
  }
  // monthly: `M H D * *`
  if (mon === '*' && dow === '*' && /^\d+$/.test(dom) && /^\d+$/.test(min) && /^\d+$/.test(hr)) {
    return { mode: 'monthly', hour: hrNum, minute: minNum, dow: [1], dom: domNum };
  }
  return { mode: 'advanced', ...empty };
}

function describe(cron: string): string {
  if (!cron || !cron.trim()) return 'Only when triggered manually.';
  try {
    return cronstrue.toString(cron.trim(), { use24HourTimeFormat: true });
  } catch {
    return 'Invalid cron expression.';
  }
}
